import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import appConfig from '../config/appConfig.json';
import './TransitCarousel.css';

const BACKEND_URL = appConfig.REACT_APP_BACKEND_URL;

// Planet icons mapping
const PLANET_ICONS = {
    'Sun': '☉',
    'Moon': '☽',
    'Mars': '♂',
    'Mercury': '☿',
    'Jupiter': '♃',
    'Venus': '♀',
    'Saturn': '♄',
    'Rahu': '☊',
    'Ketu': '☋'
};

// Calculate duration string from actual transit dates
const getDuration = (transitDate, transitEndDate) => {
    if (!transitDate) return '';

    const start = transitDate?._seconds ? new Date(transitDate._seconds * 1000) : new Date(transitDate);
    const startStr = start.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

    if (!transitEndDate) return startStr;

    const end = transitEndDate?._seconds ? new Date(transitEndDate._seconds * 1000) : new Date(transitEndDate);

    if (start.toDateString() === end.toDateString()) return startStr;

    const endStr = end.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    return `${startStr} - ${endStr}`;
};

// Simple markdown parser for bold text
const parseMarkdown = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        return part;
    });
};

// Calculate expiry text based on display end date
const getExpiryText = (endDisplayDate) => {
    if (!endDisplayDate) return null;
    const endDate = endDisplayDate._seconds
        ? new Date(endDisplayDate._seconds * 1000)
        : new Date(endDisplayDate);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return 'Expires today';
    if (diffDays === 1) return 'Expires tomorrow';
    if (diffDays <= 7) return `Expires in ${diffDays} days`;
    return null; // Don't show if > 7 days
};

const TransitCarousel = ({ userId }) => {
    const navigate = useNavigate();
    const [transits, setTransits] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const intervalRef = useRef(null);

    // Fetch active transits
    useEffect(() => {
        const fetchTransits = async () => {
            try {
                const response = await fetch(`${BACKEND_URL}/transits/active`);
                const data = await response.json();
                setTransits(data.transits || []);
            } catch (error) {
                console.error('Error fetching transits:', error);
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            fetchTransits();
        }
    }, [userId]);

    // Auto-rotate carousel every 3 seconds
    useEffect(() => {
        if (transits.length <= 1 || isPaused) return;

        intervalRef.current = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % transits.length);
        }, 3000);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [transits.length, isPaused]);

    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    // Minimum swipe distance (in px)
    const minSwipeDistance = 50;

    const handleDotClick = (index) => setCurrentIndex(index);
    const handleMouseEnter = () => setIsPaused(true);
    const handleMouseLeave = () => setIsPaused(false);

    // Navigate to transit report page
    const handleViewTransit = (transitId) => {
        navigate(`/transit-report?id=${transitId}`);
    };

    const onTouchStart = (e) => {
        setTouchEnd(null); // Reset
        setTouchStart(e.targetTouches[0].clientX);
        setIsPaused(true); // Pause auto-rotation on touch
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            // Next slide
            setCurrentIndex((prev) => (prev + 1) % transits.length);
        }
        if (isRightSwipe) {
            // Prev slide
            setCurrentIndex((prev) => (prev - 1 + transits.length) % transits.length);
        }

        setIsPaused(false);
    };

    if (!userId || loading || transits.length === 0) {
        return null;
    }

    return (
        <div
            className="transit-carousel-container"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            <div className="transit-carousel-header">
                <span className="transit-header-icon">🌟</span>
                <h3>Recent & Upcoming Transits</h3>
            </div>

            <div className="transit-carousel-wrapper">
                <div
                    className="transit-carousel-track"
                    style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                    {transits.map((transit) => (
                        <div key={transit.id} className="transit-card">
                            <div className="transit-card-main">
                                <div className="transit-card-icon">
                                    {PLANET_ICONS[transit.planet] || '⭐'}
                                </div>
                                <div className="transit-card-content">
                                    <h4 className="transit-title">{parseMarkdown(transit.title)}</h4>
                                    <p className="transit-duration">
                                        📅 {getDuration(transit.transitDate, transit.transitEndDate)}
                                        {/* Show expiry if applicable */}
                                        {(() => {
                                            const expiry = getExpiryText(transit.endDisplayDate);
                                            if (expiry) {
                                                return (
                                                    <span className="transit-expiry-tag">
                                                        • ⏳ {expiry}
                                                    </span>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </p>
                                    <p className="transit-summary">
                                        {parseMarkdown(transit.subtitle)}
                                    </p>
                                </div>
                            </div>
                            <div className="transit-card-actions">
                                <button
                                    className="transit-btn transit-btn-primary"
                                    onClick={() => handleViewTransit(transit.id)}
                                >
                                    Know More →
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {transits.length > 1 && (
                <div className="transit-carousel-dots">
                    {transits.map((_, index) => (
                        <button
                            key={index}
                            className={`transit-dot ${index === currentIndex ? 'active' : ''}`}
                            onClick={() => handleDotClick(index)}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default TransitCarousel;
